"use client";

import { useId, useState } from "react";
import { NumberStepper } from "@tripcart/traveler-ui/patterns/number-stepper";

import { useTranslator } from "@/lib/translation-context";

import { InfoTooltip } from "../../_info-tooltip.client";

import styles from "./_traveler-row.module.css";

interface TravelerRowProps {
  title: string;
  /** Operator-authored age range (e.g. "Age 18-99"). Rendered muted under
   *  the category name; omitted when the operator hasn't set one. */
  subtitle?: string;
  /** The operator's own description of who this category is for. Behind the
   *  shared info affordance rather than inline: operators write anything from
   *  three words to a paragraph, and an unbounded line on every row would
   *  push Apply below the fold on a package with several categories. */
  description?: string;
  /** What one step of this category buys, in traveller terms — "2 people
   *  each" for a Couple, "Fits 4" for a Cabin. Absent for per-person
   *  categories, where one step is self-evidently one person. */
  unitLabel?: string;
  /** One step's price, already formatted. Set only when the tour sells a
   *  single package, so the price cannot belong to a package the traveller
   *  has not picked. */
  price?: string;
  count: number;
  onIncrement: () => void;
  onDecrement: () => void;
  min?: number;
  /** False when one more step of this category would push the party past the
   *  capacity cap — which happens BEFORE the cap is reached for per-unit and
   *  per-group categories (an 8th Couple is 2 heads on a 14/15 party). */
  canIncrement?: boolean;
  /** Why "+" is disabled, already translated, when the obstacle belongs to THIS
   *  row — "needs 4 more seats" differs per category. Rendered as a line rather
   *  than a tooltip because a tooltip is unreachable by keyboard and touch,
   *  which is how the original dead "+" gave no explanation at all.
   *
   *  A reason shared by every row (the whole party is at the tour's limit) is
   *  NOT this prop — it belongs above the list, stated once, and reaches the
   *  button through `describedBy`. */
  blockedReason?: string;
  /** Id of an element OUTSIDE this row that already explains the block. Set
   *  when the picker states a party-wide limit above the list; this row then
   *  renders no line of its own and points "+" at that sentence instead of
   *  repeating it on every category. */
  describedBy?: string;
  /** `menu` sits inside the picker popover, inset from its edge. `inline`
   *  sits straight in the rail, flush with the field label above it. */
  layout?: "menu" | "inline";
}

/**
 * Why the blocked reason is a LIVE REGION keyed on a refusal count.
 *
 * `role="status"` so a REPEATED refused press is still answered. Focus alone
 * covers only the first: pressing an already-focused button fires no focus
 * event and nothing re-announces, and pressing again is the natural reaction to
 * a control that appeared to do nothing.
 *
 * A live region announces on CHANGE, and the reason does not change between two
 * presses — so the refusal count keys the SPAN INSIDE the region, and React
 * replaces that. The region itself stays mounted for the life of the row.
 *
 * That distinction is the whole fix. Keying the region meant React unmounted
 * and re-inserted the `role="status"` element itself, which is the documented
 * pattern screen readers do NOT announce: a live region has to be in the
 * accessibility tree before its content changes. The test asserted the DOM node
 * had changed, which was true and proved nothing about what was spoken.
 *
 * NOT VERIFIED HERE. This follows the documented announcing form, but no real
 * screen reader ran against it — there is none in this environment. Treat it as
 * unproven until someone tests it with NVDA, JAWS or VoiceOver.
 */

export function TravelerRow({
  title,
  subtitle,
  description,
  unitLabel,
  price,
  count,
  onIncrement,
  onDecrement,
  min = 0,
  canIncrement = true,
  blockedReason,
  describedBy,
  layout = "menu",
}: TravelerRowProps) {
  // The unit hint steps aside while a blocker is showing. "Fits 4 people" is
  // static reference, "Needs 4 more seats" is the live obstacle, and stacked
  // under the age range they make a three-line paragraph out of one row — the
  // one that matters loses. Blocked rows show the age range and the blocker.
  // The predicate is a type guard, not `Boolean`: TypeScript does not narrow
  // through `.filter(Boolean)`, so the prop had been widened to accept `null`
  // on a doc claim that a null would render as an empty string. It would
  // actually render a stray " · " separator — the widened type accepted exactly
  // the input the component cannot draw. Nothing null ever reaches it; this
  // makes the types say so.
  const meta = [subtitle, blockedReason ? null : unitLabel].filter(
    (entry): entry is string => Boolean(entry),
  );
  // From useId, not from the title. Deriving it from operator text collided
  // whenever two categories differed only in punctuation ("Couple" / "Couple!")
  // and produced `traveler-row--reason` for a whitespace-only label — either
  // way `aria-describedby` could point a screen reader at another row's reason.
  // Bumped by each refused press, purely so the live region below has
  // something to change — see the note on it.
  const [refusals, setRefusals] = useState(0);
  const t = useTranslator();
  const generatedId = useId();
  const reasonId = blockedReason ? `${generatedId}-reason` : undefined;
  // Only while "+" is actually refused: the rest of the time there is no block
  // to describe, and pointing at a party-limit sentence that does not apply
  // would announce a restriction the traveller is not under.
  const blockDescribedBy = canIncrement ? undefined : (reasonId ?? describedBy);

  return (
    // Guide `.tv-r` — `padding: 14px 0` with a HAIRLINE under each row, and
    // none under the last. Without it a three-category picker reads as one
    // block of text with numbers floating beside it; the rule is what makes
    // each line a row you can aim a thumb at.
    <div className={styles.row} data-layout={layout}>
      <RowLabel
        title={title}
        description={description}
        meta={meta}
        reasonId={reasonId}
        blockedReason={blockedReason}
        refusals={refusals}
      />
      <div className={styles.controls}>
        {price ? <span className={styles.price}>{price}</span> : null}
        {/* A typed value is walked one step at a time, because each step is
            checked against the cap. */}
        <NumberStepper
          size="sm"
          shape="round"
          value={count}
          min={min}
          onValueChange={(next) => {
            const step = next > count ? onIncrement : onDecrement;
            for (let i = Math.abs(next - count); i > 0; i--) step();
          }}
          aria-label={t("Quantity for {name}", { name: title })}
          decrementLabel={t("Decrease {name}", { name: title })}
          incrementLabel={t("Increase {name}", { name: title })}
          incrementDisabled={!canIncrement}
          incrementDescription={blockDescribedBy}
          onIncrementRefused={() => setRefusals((n) => n + 1)}
        />
      </div>
    </div>
  );
}

/** The row's left column: name, meta line, and the blocked reason.
 *
 *  Its own component because a row is two things side by side, and keeping
 *  each one whole is what makes either readable. */
function RowLabel({
  title,
  description,
  meta,
  reasonId,
  blockedReason,
  refusals,
}: {
  title: string;
  description?: string;
  /** The name's qualifiers, already filtered by the row. */
  meta: string[];
  reasonId?: string;
  blockedReason?: string;
  /** Changes on each refused press, so the live region has something to
   *  announce — see `TravelerRow`'s doc block. */
  refusals: number;
}) {
  return (
    <div className={styles.info}>
      {/* Guide `.tv-n` — 13.5px semibold, with 5px to the help mark. Ours ran
        at the inherited 16px/medium, which made a category name compete with
        the price above it instead of reading as a row label. */}
      <div className={styles.titleLine}>
        <span className={styles.title}>{title}</span>
        {description ? <InfoTooltip description={description} /> : null}
      </div>
      {/* Guide `.tv-s` — the age band is 12px, a clear step under the name.
        At 14px the two lines were nearly the same size and the row read as
        two facts rather than a label and its qualifier. */}
      {meta.length > 0 && <div className={styles.meta}>{meta.join(" · ")}</div>}
      {/* Muted, not an error colour: the traveller has done nothing wrong —
          the departure is simply too full for a unit this size. States the
          fact and stops there, no "try again later".

          The region is ALWAYS MOUNTED and only its text changes — see
          `TravelerRow`'s doc block. It was conditionally rendered and keyed on
          the refusal count, which replaces the region itself; a live region has
          to be in the accessibility tree BEFORE its content changes for the
          change to be announced, so replacing it is the documented
          non-announcing pattern. */}
      <div id={reasonId} role="status" className={styles.reason}>
        {/* The CHILD is keyed, not the region. React replaces this span on
            each refusal, which is a content change inside a region that was
            already in the accessibility tree — the form that announces. */}
        {blockedReason ? <span key={refusals}>{blockedReason}</span> : null}
      </div>
    </div>
  );
}
